/**
 * CetusEd Admin Console — Webhook Endpoint Management
 *
 * COMPLIANCE: Webhooks deliver real-time notifications to external systems.
 *   Each webhook endpoint must be configured with a shared secret for
 *   payload verification. Webhook secrets are stored encrypted at rest.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWebhookConfig } from '../../api/hooks';

export const WebhookConfig: React.FC = () => {
  const [districtId, setDistrictId] = useState('');
  const { data: config, isLoading, error } = useWebhookConfig(districtId);

  const handleDistrictSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDistrictId(e.target.value);
  }, []);

  if (isLoading) return <div className="admin-loading">Loading webhook config...</div>;

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/integrations">Integrations</Link> / Webhooks
      </nav>

      <div className="page-header">
        <h1>Webhook Configuration</h1>
      </div>

      <div className="form-field">
        <label htmlFor="wh-district">Select District</label>
        <select id="wh-district" value={districtId} onChange={handleDistrictSelect}>
          <option value="">Select a district...</option>
        </select>
      </div>

      {districtId && config && (
        <div className="detail-card">
          <h3>Endpoints ({config.endpoints.length})</h3>
          {config.endpoints.length === 0 ? (
            <p className="empty-state">No webhook endpoints configured.</p>
          ) : (
            <table className="data-table" role="table">
              <thead>
                <tr>
                  <th scope="col">URL</th>
                  <th scope="col">Events</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {config.endpoints.map((ep) => (
                  <tr key={ep.id}>
                    <td><code>{ep.url}</code></td>
                    <td>{ep.events.join(', ')}</td>
                    <td>
                      <span className={`status-badge status-badge--${ep.enabled ? 'active' : 'deactivated'}`}>
                        {ep.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {districtId && error && (
        <div className="admin-error" role="alert">
          Unable to load webhook configuration.
        </div>
      )}
    </div>
  );
};
